import { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
  /** Optional friendly route name shown in the heading, e.g. "创作台". */
  routeName?: string;
}

// Per-route error boundary. Catches render-time errors so a crash in one view
// doesn't kill the entire shell — sidebar/topbar/nav stay functional, the
// user can navigate to another view, and there's a retry button that resets
// the boundary without reloading the whole tab.
//
// Pair with the global <ErrorBoundary> in main.tsx, which catches anything
// that escapes a route boundary (e.g. shell-level crashes).
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    console.error("[RouteErrorBoundary]", this.props.routeName ?? "(route)", error);
    if (info?.componentStack) console.error(info.componentStack);
  }

  retry = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex h-full min-h-[60vh] items-center justify-center p-6">
        <div className="flex max-w-xl flex-col gap-4 rounded-lg border border-destructive/30 bg-card p-6 text-center">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full border border-destructive/30 bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-semibold text-foreground">
              {this.props.routeName ? `${this.props.routeName}加载失败` : "页面加载失败"}
            </h2>
            <p className="text-sm text-muted-foreground">
              其他页面仍可访问，可点击下方按钮重试。
            </p>
          </div>
          <details className="rounded bg-input p-3 text-left">
            <summary className="cursor-pointer text-xs text-muted-foreground">
              错误详情
            </summary>
            <pre className="mt-2 overflow-auto font-mono text-[11px] leading-relaxed text-foreground/85">
              {this.state.error.message}
            </pre>
          </details>
          <div className="flex justify-center gap-2">
            <button
              type="button"
              onClick={this.retry}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              重试
            </button>
            <button
              type="button"
              onClick={() => location.reload()}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-white/5"
            >
              刷新整页
            </button>
          </div>
        </div>
      </div>
    );
  }
}
